import {Fragment} from "react";
import {BulkDeletePreviewResult} from "../../../../../services/UrlsService";

const MAX_LISTED_ROWS = 8;

type BulkDeleteReportProps = {
  readonly preview: BulkDeletePreviewResult;
};

const renderConflictRows = (conflicts: BulkDeletePreviewResult["conflicts"]): JSX.Element => (
  <Fragment>
    {conflicts.slice(0, MAX_LISTED_ROWS).map((conflict) => (
      <tr key={conflict.id}>
        <td>{conflict.id}</td>
        <td>
          <span className="bulk-delete-reason-tag">{conflict.reason}</span>
        </td>
      </tr>
    ))}
    {conflicts.length > MAX_LISTED_ROWS && (
      <tr>
        <td colSpan={2} className="bulk-delete-more">
          … and {conflicts.length - MAX_LISTED_ROWS} more
        </td>
      </tr>
    )}
  </Fragment>
);

const renderNotFoundRows = (notFound: string[]): JSX.Element => (
  <Fragment>
    {notFound.slice(0, MAX_LISTED_ROWS).map((id) => (
      <tr key={id}>
        <td>{id}</td>
      </tr>
    ))}
    {notFound.length > MAX_LISTED_ROWS && (
      <tr>
        <td className="bulk-delete-more">… and {notFound.length - MAX_LISTED_ROWS} more</td>
      </tr>
    )}
  </Fragment>
);

const BulkDeleteReport = (props: BulkDeleteReportProps): JSX.Element => {
  const {preview} = props;

  return (
    <Fragment>
      <div className="bulk-delete-tiles">
        <div className="bulk-delete-tile good">
          <div className="bulk-delete-tile-number">{preview.eligibleCount.toLocaleString()}</div>
          <div className="bulk-delete-tile-label">Eligible to delete</div>
        </div>
        <div className="bulk-delete-tile info">
          <div className="bulk-delete-tile-number">{preview.currentlyCachedCount.toLocaleString()}</div>
          <div className="bulk-delete-tile-label">Currently cached</div>
        </div>
        <div className="bulk-delete-tile warn">
          <div className="bulk-delete-tile-number">{preview.conflicts.length.toLocaleString()}</div>
          <div className="bulk-delete-tile-label">Conflicts (skipped)</div>
        </div>
        <div className="bulk-delete-tile grey">
          <div className="bulk-delete-tile-number">{preview.notFound.length.toLocaleString()}</div>
          <div className="bulk-delete-tile-label">Not found</div>
        </div>
      </div>
      <div className="bulk-delete-subcounts">
        <div className="bulk-delete-subcount">Invalid rows dropped in parse: {preview.invalidRows.toLocaleString()}</div>
        <div className="bulk-delete-subcount">Duplicate rows removed: {preview.duplicatesRemoved.toLocaleString()}</div>
      </div>
      {(preview.conflicts.length > 0 || preview.notFound.length > 0) && (
        <div className="bulk-delete-details">
          {preview.conflicts.length > 0 && (
            <details>
              <summary>
                <span className="bulk-delete-chevron" aria-hidden="true">▸</span>
                Conflicts — cached but a field differs
                <span className="bulk-delete-details-badge warn">{preview.conflicts.length}</span>
              </summary>
              <div className="bulk-delete-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>cached_urls id</th>
                      <th>mismatch</th>
                    </tr>
                  </thead>
                  <tbody>{renderConflictRows(preview.conflicts)}</tbody>
                </table>
              </div>
            </details>
          )}
          {preview.notFound.length > 0 && (
            <details>
              <summary>
                <span className="bulk-delete-chevron" aria-hidden="true">▸</span>
                Not found — no longer cached, or a different user
                <span className="bulk-delete-details-badge grey">{preview.notFound.length}</span>
              </summary>
              <div className="bulk-delete-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>cached_urls id</th>
                    </tr>
                  </thead>
                  <tbody>{renderNotFoundRows(preview.notFound)}</tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default BulkDeleteReport;
